import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';

const Wrap = styled.div`
  border: 1px solid ${p => p.theme.line};
  border-radius: 8px;
  background: ${p => p.theme.bg};
  overflow: hidden;
  &:focus-within {
    border-color: ${p => p.theme.gold};
    box-shadow: 0 0 0 3px ${p => p.theme.goldSoft};
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
  border-bottom: 1px solid ${p => p.theme.line};
  background: ${p => p.theme.surface};
`;

const TBtn = styled.button`
  border: 1px solid transparent;
  background: ${p => p.$active ? p.theme.goldSoft : 'transparent'};
  color: ${p => p.$active ? p.theme.ink : p.theme.inkSoft};
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  min-width: 30px;
  transition: all .12s;
  &:hover { background: ${p => p.theme.surfaceAlt}; color: ${p => p.theme.ink}; }
  &:disabled { opacity: .35; cursor: not-allowed; }
`;

const Sep = styled.span`
  width: 1px;
  background: ${p => p.theme.line};
  margin: 4px 4px;
`;

const Editor = styled.div`
  .ProseMirror {
    padding: 14px 16px;
    min-height: 180px;
    color: ${p => p.theme.ink};
    font-size: 14px;
    line-height: 1.6;
    outline: none;
    font-family: 'Inter', -apple-system, sans-serif;
    p { margin: 0 0 8px; }
    h1, h2, h3 { font-family: 'Cormorant Garamond', Georgia, serif; margin: 12px 0 6px; font-weight: 600; }
    h1 { font-size: 22px; }
    h2 { font-size: 19px; }
    h3 { font-size: 17px; }
    ul, ol { padding-left: 22px; margin: 4px 0 8px; }
    li { margin: 2px 0; }
    a { color: ${p => p.theme.gold}; text-decoration: underline; }
    blockquote {
      border-left: 3px solid ${p => p.theme.gold};
      padding-left: 12px;
      margin: 8px 0;
      color: ${p => p.theme.muted};
      font-style: italic;
    }
    strong { font-weight: 700; }
    em { font-style: italic; }
    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      color: ${p => p.theme.muted};
      float: left;
      pointer-events: none;
      height: 0;
    }
  }
`;

export default function RichTextEditor({ value, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder: placeholder || 'Escreva aqui…' }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes('link').href;
    const url = window.prompt('URL do link', previous || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <Wrap>
      <Toolbar>
        <TBtn type="button" onClick={() => editor.chain().focus().toggleBold().run()} $active={editor.isActive('bold')} title="Negrito (Ctrl+B)"><b>B</b></TBtn>
        <TBtn type="button" onClick={() => editor.chain().focus().toggleItalic().run()} $active={editor.isActive('italic')} title="Itálico (Ctrl+I)"><i>I</i></TBtn>
        <TBtn type="button" onClick={() => editor.chain().focus().toggleStrike().run()} $active={editor.isActive('strike')} title="Tachado"><s>S</s></TBtn>
        <Sep />
        <TBtn type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} $active={editor.isActive('heading', { level: 2 })} title="Título">H2</TBtn>
        <TBtn type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} $active={editor.isActive('heading', { level: 3 })} title="Subtítulo">H3</TBtn>
        <Sep />
        <TBtn type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} $active={editor.isActive('bulletList')} title="Lista">•</TBtn>
        <TBtn type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} $active={editor.isActive('orderedList')} title="Lista numerada">1.</TBtn>
        <TBtn type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} $active={editor.isActive('blockquote')} title="Citação">"</TBtn>
        <Sep />
        <TBtn type="button" onClick={setLink} $active={editor.isActive('link')} title="Link">↗</TBtn>
        <TBtn type="button" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Limpar formatação">⌫</TBtn>
      </Toolbar>
      <Editor>
        <EditorContent editor={editor} />
      </Editor>
    </Wrap>
  );
}
